import {autoserializeAs, autoserializeUsing, inheritSerialization} from "dcerialize";
import type {DateTime} from "luxon";
import {DateOnlySerializer} from "../../helpers/DateSerializers";
import {IdOnlyBactoforceLocation, SimpleBactoforceLocation} from "../bactoforce/BactoforceLocation";
import {BasicFile} from "../base/BasicFile";
import type {Existing, IdentifiableModel, IdOf, New} from "../base/IdentifiableModel";
import {createExisting, createNew} from "../base/IdentifiableModel";
import type {ModificationTrackedModel} from "../base/ModificationTrackedModel";
import {ModificationDetails} from "../ModificationDetails";
import {IdOnlyCurrency} from "./Currency";

export class IdOnlyPriceList implements IdentifiableModel {
  @autoserializeAs(() => Number)
  public id?: number;

  public static existing(id: IdOf<IdOnlyPriceList>): Existing<IdOnlyPriceList> {
    return createExisting(IdOnlyPriceList, id);
  }
}

@inheritSerialization(() => IdOnlyPriceList)
export class WritablePriceList extends IdOnlyPriceList {
  @autoserializeAs(() => String)
  public name: string;
  @autoserializeAs(() => Boolean)
  public isActive: boolean;
  @autoserializeAs(() => Boolean)
  public isStandard: boolean;
  @autoserializeAs(() => IdOnlyCurrency)
  public currency: IdOnlyCurrency;
  @autoserializeAs(() => IdOnlyBactoforceLocation)
  public bactoforceLocation: Existing<IdOnlyBactoforceLocation>;

  public static createNew(name: string,
                          currency: Readonly<Existing<IdOnlyCurrency>>,
                          bactoforceLocation: Readonly<Existing<IdOnlyBactoforceLocation>>,
                          isStandard: boolean): New<WritablePriceList> {
    const priceList = createNew(WritablePriceList);
    priceList.name = name;
    priceList.isStandard = isStandard;
    priceList.isActive = true;
    priceList.currency = currency;
    priceList.bactoforceLocation = bactoforceLocation;
    return priceList;
  }
}

@inheritSerialization(() => WritablePriceList)
export class SimplePriceList extends WritablePriceList {
  @autoserializeAs(() => SimpleBactoforceLocation)
  public override bactoforceLocation: Existing<SimpleBactoforceLocation>;
}


/**
 * @sealed
 */
@inheritSerialization(() => SimplePriceList)
export class PriceList extends SimplePriceList implements ModificationTrackedModel {
  @autoserializeAs(() => Number)
  public readonly numberOfAssignedCustomerLocations: number;
  @autoserializeAs(() => ModificationDetails)
  public readonly modificationDetails?: ModificationDetails;
}

export abstract class BaseBulkPriceUpdateRequest {
  @autoserializeAs(() => IdOnlyBactoforceLocation)
  public bactoforceLocation: Existing<IdOnlyBactoforceLocation>;
  @autoserializeUsing(DateOnlySerializer)
  public asOfDate: DateTime<true>;
  @autoserializeAs(() => Boolean)
  public dryRun: boolean;

  protected constructor(bfLocation: Readonly<Existing<IdOnlyBactoforceLocation>>, asOfDate: DateTime<true>, dryRun: boolean) {
    this.bactoforceLocation = bfLocation;
    this.asOfDate = asOfDate;
    this.dryRun = dryRun;
  }
}

/**
 * @sealed
 */
@inheritSerialization(() => BaseBulkPriceUpdateRequest)
export class BulkUpdatePriceListsRequest extends BaseBulkPriceUpdateRequest {
  @autoserializeAs(() => BasicFile)
  public file: BasicFile;

  public constructor(bfLocation: Readonly<Existing<IdOnlyBactoforceLocation>>, asOfDate: DateTime<true>, dryRun: boolean, file: Readonly<BasicFile>) {
    super(bfLocation, asOfDate, dryRun);
    this.file = file;
  }
}

/**
 * @sealed
 */
@inheritSerialization(() => BaseBulkPriceUpdateRequest)
export class BulkUpdateSpecialPricesRequest extends BaseBulkPriceUpdateRequest {
  @autoserializeAs(() => Number)
  public changeByPercentage: number;

  public constructor(bfLocation: Readonly<Existing<IdOnlyBactoforceLocation>>,
                     asOfDate: DateTime<true>,
                     dryRun: boolean,
                     changeByPercentage: number) {
    super(bfLocation, asOfDate, dryRun);
    this.changeByPercentage = changeByPercentage;
  }
}
